import '../styles/PageGlobal.css';
import './ProduitListPage.css';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReferenceButton from '../components/ReferenceButton';
import { ProductService } from '../services/ProductService';
import { ImportService } from '../services/ImportService.js';
import { ExportService } from '../services/ExportService.js';

export default function ProduitListPage() {
  const navigate = useNavigate();

  const importService = useMemo(() => new ImportService(), []);
  const exportService = useMemo(() => new ExportService(), []);

  const [produits, setProduits] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // ── Data loader (Real Backend Call) ─────────────────────────────────────────
  const fetchProduits = useCallback(async () => {
    const produitService = new ProductService();
    setLoading(true);
    setError(null);
    try {
      const data = await produitService.getAllProducts();
      setProduits(data);
    } catch (err) {
      console.error('Error fetching produits:', err);
      setError(err.message || 'Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProduits();
  }, [fetchProduits]);

  // ── Handlers ──────────────────────────────────────────────────────────────
   const handleImportProduits = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const message = await importService.importProducts(file);
        alert(message);
        fetchProduits(); // Rafraîchir la liste
      } catch (err) {
        alert("Erreur d'import : " + err.message);
      }
    };
    input.click();
  };

  const handleExportProduits = async () => {
    try {
      const blob = await exportService.exportProducts();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'produits_export.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      alert("Erreur d'export : " + err.message);
    }
  };

  const handleEdit = (id) => {
    navigate(`/product_edit/${id}`);
  };

  // Delete Handler with confirmation
  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible.")) {
      return;
    }
    
    try {
      const produitService = new ProductService();
      await produitService.deleteProduct(id);
      
      // Refresh the list after successful deletion
      await fetchProduits();
      alert("Produit supprimé avec succès !");
    } catch (err) {
      console.error('Error deleting produit:', err);
      alert(err.message || "Erreur lors de la suppression du produit");
    }
  };

   const handleDeleteAll = async () => {
    if (!window.confirm("ATTENTION : Cela va tenter de supprimer tous les produits. Ceux liés à des stocks seront ignorés. Continuer ?")) return;
    
    try {
      const result = await new ProductService().deleteAllProducts();
      
      if (result.success) {
        alert(` Succès :\n${result.message}`);
        fetchProduits(); // Rafraîchir la liste
      } else {
        alert(` Échec :\n${result.message}`);
      }
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  
  const handleNouvelleProduit = () => {
    navigate('/product_new');
  };

  // ── Render guards ────────────────────────────────────────────────────────────
  if (loading) return <div>Chargement…</div>;
  if (error)   return <div className="produit-error">{error}</div>;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="containerMain produit-container">
      <h1 className="produit-title">Produits</h1>

      {/* Top buttons */}
      <div className="produit-top-buttons">
        <ReferenceButton onClick={handleImportProduits}>Import des produits</ReferenceButton>
        <ReferenceButton onClick={handleExportProduits}>Exporter des produits</ReferenceButton>
        <button className="produit-btn produit-btn-red" onClick={handleDeleteAll} style={{ fontWeight: 'bold' }}>
      🗑️ Supprimer TOUS les produits
    </button>
      </div>

      {/* Single table */}
      <div className="produit-table-wrapper">
        <table className="produit-table">
          <thead>
            <tr>
              <th className="produit-th-normal">N</th>
              <th><div className="produit-th-vert">REF FABRICANT</div></th>
              <th><div className="produit-th-vert">REF INTERNE</div></th>
              <th><div className="produit-th-vert">DESIGNATION</div></th>
              <th><div className="produit-th-vert">DESCRIPTION</div></th>
              <th><div className="produit-th-vert">CAPACITE</div></th>
              <th><div className="produit-th-vert">TYPE DE PRODUIT</div></th>
              <th><div className="produit-th-vert">CATEGORIES</div></th>
              <th><div className="produit-th-vert">SOUS CATEGORIES</div></th>
              <th><div className="produit-th-vert">UNITE DE MESURE</div></th>
              <th><div className="produit-th-vert">PA</div></th>
              <th><div className="produit-th-vert">PV</div></th>
              <th><div className="produit-th-vert">ECOPARTICIPATION</div></th>
              <th><div className="produit-th-vert">DEEE</div></th>
              <th><div className="produit-th-vert">COUT DE REVIENT</div></th>
              <th><div className="produit-th-vert">EAN</div></th>
              <th><div className="produit-th-vert">HSCOD</div></th>
              <th><div className="produit-th-vert">PHOTOS</div></th>
              <th><div className="produit-th-vert">POIDS</div></th>
              <th><div className="produit-th-vert">LONGUEUR</div></th>
              <th><div className="produit-th-vert">LARGEUR</div></th>
              <th><div className="produit-th-vert">HAUTEUR</div></th>
              <th><div className="produit-th-vert">POIDS / VOLUME</div></th>
              <th><div className="produit-th-vert">GARANTIE</div></th>
              <th className="produit-th-normal">Action</th>
            </tr>
          </thead>
          <tbody>
            {produits.map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td className="produit-ref">{p.refFabricant || ''}</td>
                <td>{p.refInterne || ''}</td>
                <td>{p.designation || ''}</td>
                <td>{p.description || ''}</td>
                <td>{p.capacite || ''}</td>
                <td>{p.typeDeproduit || p.typeDeProduit || ''}</td>
                <td>{p.categories || ''}</td>
                <td>{p.sousCategories || ''}</td>
                <td>{p.uniteDeMesure || ''}</td>
                <td>{p.pa || ''}</td>
                <td>{p.pv || ''}</td>
                <td>{p.ecoparticipation || ''}</td>
                <td>{p.deee || ''}</td>
                <td>{p.coutDeRevient || ''}</td>
                <td>{p.ean || ''}</td>
                <td>{p.hscod || ''}</td>
                <td>{p.photos || ''}</td>
                <td>{p.poids || ''}</td>
                <td>{p.longueur || ''}</td>
                <td>{p.largeur || ''}</td>
                <td>{p.hauteur || ''}</td>
                <td>{p.poidsVolume || ''}</td>
                <td>{p.garantie || ''}</td>
                <td className="produit-action-cell">
                  <button className="produit-btn produit-btn-blue" onClick={() => handleEdit(p.id)}>Edit</button>
                  {/*  Delete button with red styling */}
                  <button className="produit-btn produit-btn-red" onClick={() => handleDelete(p.id)}>Supprimer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bottom right button */}
      <div className="produit-bottom-buttons">
        <ReferenceButton onClick={handleNouvelleProduit}>Nouvelle produit</ReferenceButton>
      </div>
    </div>
  );
}